import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import { IconCalendarEvent, IconChevronDown } from '@tabler/icons-react';
import EmailEditor from 'react-email-editor';
import * as Sentry from '@sentry/react';
import { CAMPAIGN_SEND_STATUS } from 'common/constants';

import Input from '../inputs/Input';
import Button from '../buttons/Button';
import Loader from '../loader/Loader';
import AlertMessage from '../alerts/AlertMessage';
import { useAccount } from '../../store/account/hooks';
import { usePages } from '../../store/pages/hooks';
import { useRecommendedTasks } from '../../store/recommended-tasks/hooks';
import { getEditorOptions, getLinkTypes, getNetworkError } from '../../core/utils';
import { getPageUrl } from '../../core/utils/pages';
import { registerGalleryFileManager } from '../../core/utils/unlayer-file-manager';
import { updateCampaign, updateTemplate, uploadHtml } from '../../api';
import { acceptSuggestedCampaignWithTemplate, getPersonalizedTemplate } from '../emails/suggested-campaign';

const bodyValues = {
  fontFamily: {
    label: 'Lato',
    value: "'Lato', Tahoma, Verdana, sans-serif",
  },
};

/**
 * One warm-up email (a staff-authored suggested campaign) in the onboarding Welcome Series step.
 * Same accordion + inline Unlayer editor as WelcomeEmailAccordion. The first save ACCEPTS the
 * suggested campaign (copies its shared template into one the tenant owns, `suggested` -> `draft`);
 * later saves update that template. Launching still happens on the Campaigns page.
 */
const WarmUpEmailAccordion = ({ campaign, timingLabel, isOpen, onToggle, onSaveSuccess }) => {
  const editorRef = useRef(null);
  const [editorLoading, setEditorLoading] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [design, setDesign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ subject: '', previewText: '' });

  const { account } = useAccount();
  const { pageList } = usePages();
  const { updateReviewDraftTaskStatus } = useRecommendedTasks();

  const isSuggested = campaign.sendStatus === CAMPAIGN_SEND_STATUS.suggested;
  const editorOptions = useMemo(() => getEditorOptions({ account, campaignDetails: campaign }), [account, campaign]);

  // (Re)load the template and reset the fields every time the accordion opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setEmailConfig({
      subject: campaign.emailConfig?.subject ?? '',
      previewText: campaign.emailConfig?.previewText ?? '',
    });
    setEditorLoading(true);
    setLoadingTemplate(true);
    getPersonalizedTemplate({
      templateId: campaign.template.templateId,
      version: campaign.template.version,
      suggested: isSuggested,
      campaignId: campaign.campaignId,
      account,
    })
      .then((template) => {
        if (!cancelled) setDesign(template.design);
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (!cancelled) setError('Failed to load this email. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false);
      });
    return () => {
      cancelled = true;
    };
    // Only on open: the campaign object is refreshed by the parent after every save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const exportFromEditor = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const editor = editorRef.current?.editor;
        if (!editor) {
          reject(new Error('Editor not ready'));
          return;
        }
        editor.exportHtml(({ design: exportedDesign, html }) => resolve({ design: exportedDesign, html }));
      }),
    [],
  );

  const handleEditorReady = useCallback(
    (unlayer) => {
      registerGalleryFileManager(unlayer);
      if (design) {
        try {
          unlayer.loadDesign(JSON.parse(design));
        } catch (err) {
          Sentry.captureException(err);
        }
      }
      unlayer.setBodyValues(bodyValues);
      const donationPageUrl = getPageUrl({ account, params: { campaignId: campaign.campaignId } });
      unlayer.setLinkTypes(getLinkTypes({ donationPageUrl, pageList, account }));
      setEditorLoading(false);
    },
    [design, account, pageList, campaign.campaignId],
  );

  const handleSave = async () => {
    setSaving(true);
    setError(false);
    try {
      const { design: editedDesign, html } = await exportFromEditor();
      const nextEmailConfig = { ...campaign.emailConfig, ...emailConfig };
      const configChanged =
        nextEmailConfig.subject !== campaign.emailConfig?.subject ||
        nextEmailConfig.previewText !== campaign.emailConfig?.previewText;

      let saved = campaign;
      if (isSuggested) {
        saved = await acceptSuggestedCampaignWithTemplate({
          campaign,
          design: JSON.stringify(editedDesign),
          html,
          emailConfig: nextEmailConfig,
        });
        updateReviewDraftTaskStatus(campaign.campaignId);
      } else {
        const { uploadUrl } = await updateTemplate(
          campaign.template.templateId,
          campaign.template.version,
          editedDesign,
          nextEmailConfig,
        );
        if (uploadUrl) await uploadHtml(uploadUrl, html);
      }

      if (configChanged) {
        const { campaignId, ...body } = saved;
        await updateCampaign(campaignId, { ...body, emailConfig: nextEmailConfig });
      }

      onSaveSuccess?.();
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setSaving(false);
  };

  const toggleOnKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div data-testid={`warm-up-email-${campaign.campaignId}`}>
      <div
        className="flex w-full cursor-pointer items-center justify-between gap-4 p-8"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={toggleOnKey}
      >
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="text-h4 font-semibold">Warm-up Email {campaign.warmUpOrder}</h3>
          {timingLabel && (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-0.5 text-sm text-primary-700">
              <IconCalendarEvent size={14} aria-hidden="true" />
              {timingLabel}
            </span>
          )}
        </div>
        {isOpen ? (
          <div className="flex items-center gap-4">
            <Button
              title="Cancel"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              disabled={saving}
            />
            <Button
              title="Save Email"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              loading={saving}
              disabled={saving || loadingTemplate || editorLoading}
            />
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-4">
            <span
              className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm ${
                isSuggested ? 'bg-gray-10 text-gray-800' : 'bg-green-50 text-green-700'
              }`}
            >
              {isSuggested ? 'Not reviewed' : 'Reviewed'}
            </span>
            <IconChevronDown size={20} className="text-gray-500" aria-hidden="true" />
          </div>
        )}
      </div>

      <Collapse in={isOpen} unmountOnExit>
        <div className="border-t border-gray-100 p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Input
                id={`warm-up-subject-${campaign.campaignId}`}
                label="Subject Line"
                value={emailConfig.subject}
                onChange={(e) => setEmailConfig((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder="Enter email subject"
                size="lg"
              />
              <Input
                id={`warm-up-preview-${campaign.campaignId}`}
                label="Preview Text"
                value={emailConfig.previewText}
                onChange={(e) => setEmailConfig((prev) => ({ ...prev, previewText: e.target.value }))}
                placeholder="Add preview text"
                size="lg"
              />
            </div>

            <div className="border border-gray-200">
              {(editorLoading || loadingTemplate) && (
                <div className="flex h-[550px] items-center justify-center">
                  <Loader />
                </div>
              )}
              {!loadingTemplate && design && (
                <div className={editorLoading ? 'invisible h-0' : ''}>
                  <EmailEditor ref={editorRef} onReady={handleEditorReady} options={editorOptions} minHeight={550} />
                </div>
              )}
            </div>
          </div>

          <AlertMessage
            open={!!error}
            message={typeof error === 'string' ? error : 'Failed to save email. Please try again.'}
            onClose={() => setError(false)}
            severity="error"
          />
        </div>
      </Collapse>
    </div>
  );
};

WarmUpEmailAccordion.propTypes = {
  campaign: PropTypes.shape({
    campaignId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    warmUpOrder: PropTypes.number.isRequired,
    sendStatus: PropTypes.string.isRequired,
    sendTime: PropTypes.string,
    template: PropTypes.shape({ templateId: PropTypes.string, version: PropTypes.string }).isRequired,
    emailConfig: PropTypes.shape({ subject: PropTypes.string, previewText: PropTypes.string }),
  }).isRequired,
  timingLabel: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func,
};

export default WarmUpEmailAccordion;
